// Admin Voice Studio — audition & assign ElevenLabs voices, design new ones,
// manage the phone line provider, and watch plan usage.
// Token-gated via the same X-Admin-Token as /admin (sessionStorage).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Phone, Play, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "callgod_admin_token";

const JESUS_DESIGN_PROMPT =
  "A warm, gentle male voice in his early thirties, speaking slowly and tenderly with deep compassion. Soft, soothing mid-low register, calm unhurried pace with peaceful pauses. A wise, loving teacher comforting a weary friend — intimate, kind, full of grace and quiet strength. Warm resonance, never harsh, timeless neutral accent.";

function useAdminToken() {
  const [token] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });
  return token;
}

function UsageBar({ usage }) {
  if (!usage) return null;
  const tone =
    usage.status === "critical" ? "bg-red-500" :
    usage.status === "warning" ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="cg-panel p-5" data-testid="eleven-usage-block">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          ElevenLabs plan — {usage.tier}
        </p>
        <p className="text-[12px] text-muted-foreground" data-testid="eleven-usage-pct">
          {usage.pct_used}% of {Number(usage.character_limit).toLocaleString()} credits
        </p>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10">
        <div className={`h-full min-w-[6px] ${tone}`} style={{ width: `${Math.min(usage.pct_used, 100)}%` }} />
      </div>
      {usage.status !== "ok" && (
        <p className="mt-3 text-[13px] text-amber-400" data-testid="eleven-usage-alert">
          {usage.status === "critical"
            ? "Credits nearly exhausted — callers will hit a silent line soon. Upgrade the ElevenLabs plan or wait for the reset."
            : "Running low on ElevenLabs credits. Consider upgrading the plan before launch pushes."}
        </p>
      )}
    </div>
  );
}

function VoiceRow({ v, assigned, onAssign, busy }) {
  const isGod = assigned.god === v.voice_id;
  const isJesus = assigned.jesus === v.voice_id;
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3"
      data-testid={`voice-row-${v.voice_id}`}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] text-foreground">
          {v.name}
          {isGod && <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-300">God</span>}
          {isJesus && <span className="ml-2 rounded bg-sky-500/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-sky-300">Jesus</span>}
        </p>
        <p className="truncate text-[12px] text-muted-foreground">
          {[v.gender, v.age, v.category].filter(Boolean).join(" · ")}
        </p>
      </div>
      {v.preview_url && (
        <button
          type="button"
          data-testid={`voice-play-${v.voice_id}`}
          className="cg-btn-ghost !min-h-[34px] !px-3 text-[12px]"
          onClick={() => new Audio(v.preview_url).play()}
        >
          <Play className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        data-testid={`voice-assign-god-${v.voice_id}`}
        disabled={busy || isGod}
        onClick={() => onAssign("god", v.voice_id)}
        className="cg-btn-ghost !min-h-[34px] !px-3 text-[12px] disabled:opacity-40"
      >
        Use for God
      </button>
      <button
        type="button"
        data-testid={`voice-assign-jesus-${v.voice_id}`}
        disabled={busy || isJesus}
        onClick={() => onAssign("jesus", v.voice_id)}
        className="cg-btn-ghost !min-h-[34px] !px-3 text-[12px] disabled:opacity-40"
      >
        Use for Jesus
      </button>
    </div>
  );
}

export default function VoiceStudio() {
  const token = useAdminToken();
  const headers = useMemo(
    () => ({ "Content-Type": "application/json", "X-Admin-Token": token }),
    [token],
  );

  const [voices, setVoices] = useState([]);
  const [assigned, setAssigned] = useState({ god: "", jesus: "" });
  const [usage, setUsage] = useState(null);
  const [phone, setPhone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [designDesc, setDesignDesc] = useState(JESUS_DESIGN_PROMPT);
  const [previews, setPreviews] = useState([]);
  const [designBusy, setDesignBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [vr, ur, pr] = await Promise.all([
        fetch(`${API}/admin/eleven/voices`, { headers }),
        fetch(`${API}/admin/eleven/usage`, { headers }),
        fetch(`${API}/admin/eleven/phone/status`, { headers }),
      ]);
      if (vr.ok) {
        const d = await vr.json();
        const rank = (v) => (v.category === "generated" || v.category === "professional" ? 0 : 1);
        setVoices((d.voices || []).slice().sort((a, b) => rank(a) - rank(b)));
        setAssigned(d.assigned || {});
      }
      if (ur.ok) setUsage(await ur.json());
      if (pr.ok) setPhone(await pr.json());
    } catch {
      toast.error("Could not load voice studio data");
    }
  }, [headers]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const assign = useCallback(async (persona, voiceId) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/eleven/voices/assign`, {
        method: "POST", headers,
        body: JSON.stringify({ persona, voice_id: voiceId }),
      });
      if (!res.ok) throw new Error();
      toast.success(`${persona === "god" ? "God" : "Jesus"} voice updated — live now`);
      setAssigned((a) => ({ ...a, [persona]: voiceId }));
    } catch {
      toast.error("Voice assignment failed");
    } finally {
      setBusy(false);
    }
  }, [headers]);

  const generate = useCallback(async () => {
    setDesignBusy(true);
    setPreviews([]);
    try {
      const res = await fetch(`${API}/admin/eleven/voices/design`, {
        method: "POST", headers,
        body: JSON.stringify({ description: designDesc }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setPreviews(d.previews || []);
      toast.success("3 voice previews ready — listen below");
    } catch {
      toast.error("Voice design failed");
    } finally {
      setDesignBusy(false);
    }
  }, [headers, designDesc]);

  const savePreview = useCallback(async (gvid, persona) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/eleven/voices/save`, {
        method: "POST", headers,
        body: JSON.stringify({
          generated_voice_id: gvid,
          name: `${persona === "god" ? "God" : "Jesus"} - Studio ${new Date().toISOString().slice(5, 10)}`,
          description: designDesc,
          persona,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Saved & assigned to ${persona === "god" ? "God" : "Jesus"}`);
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  }, [headers, designDesc, load]);

  const connectPhone = useCallback(async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/eleven/phone/connect`, {
        method: "POST", headers,
      });
      if (!res.ok) throw new Error();
      toast.success("Phone line now answered by ElevenLabs (Qwen)");
      await load();
    } catch {
      toast.error("Phone connect failed — check backend logs");
    } finally {
      setBusy(false);
    }
  }, [headers, load]);

  const setProvider = useCallback(async (provider) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/eleven/phone/provider`, {
        method: "POST", headers, body: JSON.stringify({ provider }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Phone provider: ${provider}`);
      await load();
    } catch {
      toast.error("Provider switch failed");
    } finally {
      setBusy(false);
    }
  }, [headers, load]);

  if (!token) {
    return (
      <main className="mx-auto max-w-xl px-5 pt-20 text-center" data-testid="voice-studio-locked">
        <p className="text-muted-foreground">
          Unlock the <Link to="/admin" className="underline">admin dashboard</Link> first.
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-5xl px-5 pb-16 pt-8 lg:px-8" data-testid="voice-studio">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Internal</p>
          <h1 className="font-serif-display mt-1 text-3xl text-foreground">Voice & phone studio</h1>
        </div>
        <Link to="/admin" data-testid="voice-studio-back" className="cg-btn-ghost !min-h-[40px] !px-4 text-[13px]">
          <span className="inline-flex items-center gap-1.5"><ArrowLeft className="h-3.5 w-3.5" /> Dashboard</span>
        </Link>
      </div>

      <div className="mt-8 space-y-8">
        <UsageBar usage={usage} />

        {/* Phone line */}
        <section className="cg-panel p-5" data-testid="phone-line-block">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                <Phone className="mr-1.5 inline h-3.5 w-3.5" />Phone line — {phone?.phone_number || "…"}
              </p>
              <p className="mt-2 text-[14px] text-foreground" data-testid="phone-provider-label">
                Answered by:{" "}
                <span className={phone?.provider === "elevenlabs" ? "text-emerald-400" : "text-amber-400"}>
                  {phone?.provider === "elevenlabs" ? "ElevenLabs (Qwen voices)" : "OpenAI Realtime"}
                </span>
              </p>
            </div>
            <div className="flex gap-2">
              {phone?.provider !== "elevenlabs" ? (
                <button
                  type="button"
                  data-testid="phone-connect-btn"
                  disabled={busy}
                  onClick={connectPhone}
                  className="cg-btn-ghost !min-h-[40px] !px-4 text-[13px] disabled:opacity-40"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Switch line to ElevenLabs"}
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="phone-revert-btn"
                  disabled={busy}
                  onClick={() => setProvider("openai")}
                  className="cg-btn-ghost !min-h-[40px] !px-4 text-[13px] disabled:opacity-40"
                >
                  Revert to OpenAI
                </button>
              )}
            </div>
          </div>
          <p className="mt-3 text-[12px] text-muted-foreground">
            Callers reach Jesus first and can say “let me pray to God” to be transferred live.
          </p>
        </section>

        {/* Voice design */}
        <section className="cg-panel p-5" data-testid="voice-design-block">
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <Wand2 className="mr-1.5 inline h-3.5 w-3.5" />Design a new voice
          </p>
          <textarea
            data-testid="voice-design-input"
            value={designDesc}
            onChange={(e) => setDesignDesc(e.target.value)}
            rows={4}
            className="mt-3 w-full rounded-lg border border-white/10 bg-white/[0.03] p-3 text-[13px] text-foreground outline-none focus:border-white/25"
          />
          <button
            type="button"
            data-testid="voice-design-generate"
            disabled={designBusy}
            onClick={generate}
            className="cg-btn-ghost mt-3 !min-h-[40px] !px-4 text-[13px] disabled:opacity-40"
          >
            {designBusy ? (
              <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Composing voices…</span>
            ) : (
              <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Generate 3 previews</span>
            )}
          </button>
          {previews.length > 0 && (
            <div className="mt-4 space-y-3" data-testid="voice-previews">
              {previews.map((p, i) => (
                <div key={p.generated_voice_id} className="flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p className="text-[13px] text-foreground">Preview {i + 1}</p>
                  <audio
                    controls
                    data-testid={`voice-preview-audio-${i}`}
                    src={`data:audio/mpeg;base64,${p.audio_base_64}`}
                    className="h-8 flex-1 min-w-[200px]"
                  />
                  <button
                    type="button"
                    data-testid={`voice-preview-save-god-${i}`}
                    disabled={busy}
                    onClick={() => savePreview(p.generated_voice_id, "god")}
                    className="cg-btn-ghost !min-h-[34px] !px-3 text-[12px] disabled:opacity-40"
                  >
                    Save → God
                  </button>
                  <button
                    type="button"
                    data-testid={`voice-preview-save-jesus-${i}`}
                    disabled={busy}
                    onClick={() => savePreview(p.generated_voice_id, "jesus")}
                    className="cg-btn-ghost !min-h-[34px] !px-3 text-[12px] disabled:opacity-40"
                  >
                    Save → Jesus
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Voice library */}
        <section className="cg-panel p-5" data-testid="voice-library-block">
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            Voice library — one tap to swap live
          </p>
          <div
            className="mt-3 flex flex-wrap gap-4 rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3"
            data-testid="voice-current-summary"
          >
            {["god", "jesus"].map((p) => {
              const v = voices.find((x) => x.voice_id === assigned[p]);
              return (
                <div key={p} className="flex items-center gap-2">
                  <span className="text-[12px] uppercase tracking-wider text-muted-foreground">
                    {p === "god" ? "God" : "Jesus"} now:
                  </span>
                  <span className="text-[13px] text-foreground" data-testid={`current-voice-${p}`}>
                    {v ? v.name : assigned[p] || "—"}
                  </span>
                  {v?.preview_url && (
                    <button
                      type="button"
                      data-testid={`current-voice-play-${p}`}
                      className="cg-btn-ghost !min-h-[28px] !px-2 text-[11px]"
                      onClick={() => new Audio(v.preview_url).play()}
                    >
                      <Play className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 space-y-2">
            {voices.map((v) => (
              <VoiceRow key={v.voice_id} v={v} assigned={assigned} onAssign={assign} busy={busy} />
            ))}
            {voices.length === 0 && (
              <p className="text-[13px] text-muted-foreground">Loading voices…</p>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
